import { Outlet, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { AppShell, ThemeProvider, type ShellLinkComponent } from "@yk/ui";
import { Bell } from "@yk/ui/icons";
import { AuthGate, useAuthStore, hasPermission } from "@yk/frontend-core";
import { authApi } from "../lib/api.js";
import { isPublicPath } from "../lib/public-paths.js";
import { useNavItems } from "../lib/nav.js";
import {
  SidebarBrand,
  SidebarUser,
  workspaceTitle,
} from "./shell-bits.js";
import { useGlobalSearch } from "./use-global-search.js";

const linkComponent = Link as unknown as ShellLinkComponent;

function Shell(): React.JSX.Element {
  const location = useLocation();
  const navItems = useNavItems();
  const user = useAuthStore((s) => s.user);
  const search = useGlobalSearch();
  return (
    <AppShell
      navItems={navItems}
      currentPath={location.pathname}
      linkComponent={linkComponent}
      brand={<SidebarBrand roles={user?.roles} />}
      sidebarFooter={<SidebarUser />}
      title={workspaceTitle(user?.roles)}
      commandPaletteItems={search.items}
      commandPaletteQuery={search.query}
      commandPaletteOnQueryChange={search.setQuery}
      commandPaletteLoading={search.loading}
      commandPalettePlaceholder="Search complaints, users…"
      commandPaletteEmptyMessage="No matches. Try a complaint code, title, or name."
      topbarEnd={
        <button
          type="button"
          aria-label="Notifications"
          className="flex size-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-muted/10 hover:text-fg"
        >
          <Bell className="size-5" aria-hidden="true" />
        </button>
      }
      searchLabel="Search complaints, users…"
    >
      <Outlet />
    </AppShell>
  );
}

export function AppLayout(): React.JSX.Element {
  const location = useLocation();
  const isPublic = isPublicPath(location.pathname);
  const user = useAuthStore((s) => s.user);
  const brand = user?.brand ?? null;
  const can = (code: string): boolean => hasPermission(user, code);

  if (isPublic) {
    return (
      <ThemeProvider brand={brand}>
        <AuthGate
          fetchMe={authApi.fetchMe}
          isPublicPath={isPublicPath}
          signInPath="/login"
        >
          <Outlet />
        </AuthGate>
      </ThemeProvider>
    );
  }
  return (
    <ThemeProvider can={can} brand={brand}>
      <AuthGate
        fetchMe={authApi.fetchMe}
        isPublicPath={isPublicPath}
        signInPath="/login"
      >
        <Shell />
      </AuthGate>
    </ThemeProvider>
  );
}
